import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@lemonadetraining/web-shared";
import { useMovementState, type UseMovementStateResult } from "../hooks/useMovementState";

interface MovementStateContextValue {
  state: UseMovementStateResult;
  enable: () => void;
}

const MovementStateContext = createContext<MovementStateContextValue | null>(null);

// One listener on the signed-in athlete's MovementState for the whole dashboard,
// opened only once a page that shows their numbers reads it.
export function MovementStateProvider({ children }: PropsWithChildren) {
  const uid = useSelector((s: RootState) => s.user.user?.uid ?? null);
  const [enabled, setEnabled] = useState(false);
  const state = useMovementState(uid, { enabled });
  const enable = useCallback(() => setEnabled(true), []);

  const value = useMemo<MovementStateContextValue>(() => ({ state, enable }), [state, enable]);

  return <MovementStateContext.Provider value={value}>{children}</MovementStateContext.Provider>;
}

/** Reading the state opens the shared listener. */
export function useMovementStateFromContext(): UseMovementStateResult {
  const ctx = useContext(MovementStateContext);
  const enable = ctx?.enable;
  useEffect(() => {
    enable?.();
  }, [enable]);
  if (!ctx) {
    throw new Error("useMovementStateFromContext must be used inside MovementStateProvider");
  }
  return ctx.state;
}
